// src/services/data/userService.ts
import { FabrikUser, DayPassResponse, CheckInResponse, MembershipTierResponse } from '../../types';
import { fetchUsers, fetchUserById, invalidateUsersCache, fetchUserActiveDayPasses, fetchUserRecentCheckIns, fetchMembershipTiers } from '../api/endpoints/users';
import { invalidateCache } from '../api/cache';
import { store, addUser, updateUser, removeUser, clearUsers } from './store';
import { refreshAuthToken } from '../firebase';
import { PaginatedResponse } from '../api/utils';

// Type for filter options
export interface UserFilterOptions {
  email: string | null;
  userId: string | null;
  page: number;
  membersOnly?: boolean;
  includeUsageData?: boolean;
  orderBy: string;
  orderDirection: 'asc' | 'desc';
}

// Default filter options
const defaultFilters: UserFilterOptions = {
  email: null,
  userId: null,
  page: 1,
  membersOnly: false,
  includeUsageData: true,
  orderBy: 'last_name',
  orderDirection: 'asc'
};

class UserDataService {
  private token: string | null = null;
  private currentFilters: UserFilterOptions = { ...defaultFilters };
  
  // Set the authentication token
  setToken(token: string | null): void {
    this.token = token;
  }
  
  // Get the current token, refreshing it if needed
  async getToken(): Promise<string | null> {
    if (!this.token) {
      try {
        const newToken = await refreshAuthToken();
        if (newToken) {
          this.setToken(newToken);
          return newToken;
        }
      } catch (error) {
        // Silent failure
      }
    }
    return this.token;
  }
  
  // Set filter options
  setFilters(filters: Partial<UserFilterOptions>): void {
    this.currentFilters = { ...this.currentFilters, ...filters };
    invalidateCache('users');
  }
  
  // Reset filters to default
  resetFilters(): void {
    this.currentFilters = { ...defaultFilters };
    invalidateCache('users');
  }
  
  // Get current filters
  getFilters(): UserFilterOptions {
    return { ...this.currentFilters };
  }
  
  /**
   * Fetch users with current filters
   * @returns Promise containing the users and pagination info
   */
  async fetchUsers(): Promise<{
    users: FabrikUser[];
    totalPages: number;
    currentPage: number;
    totalCount: number;
  }> {
    // Set loading state
    store.metadata.isLoading.users = true;
    
    const token = await this.getToken();
    if (!token) {
      store.metadata.isLoading.users = false;
      throw new Error('Authentication token not available');
    }
    
    try {
      const { email, userId, page, membersOnly, includeUsageData, orderBy, orderDirection } = this.currentFilters;
      const response = await fetchUsers(token, page, email || undefined, userId || undefined, includeUsageData, membersOnly, orderBy, orderDirection);
      
      // Always add users to store (accumulate)
      if (response.items) {
        response.items.forEach(user => {
          addUser(user);
        });
      }
      
      // Update metadata
      store.metadata.lastFetched.users = Date.now();
      store.metadata.pagination.users = {
        currentPage: response.current_page || 1,
        totalPages: response.total_pages || 1,
        hasMore: (response.current_page || 1) < (response.total_pages || 1)
      };
      
      return {
        users: response.items || [],
        totalPages: response.total_pages || 1,
        currentPage: response.current_page || 1,
        totalCount: response.total_count || 0
      };
    } catch (error) {
      // Log detailed error information
      console.error('Error fetching users:', error);
      
      // Update metadata to indicate error state
      store.metadata.lastFetched.users = Date.now();
      
      // Re-throw with more context for better error handling in UI
      if (error instanceof Error) {
        throw new Error(`Failed to fetch users: ${error.message}`);
      } else {
        throw new Error('Failed to fetch users: Unknown error');
      }
    } finally {
      // Always reset loading state
      store.metadata.isLoading.users = false;
    }
  }
  
  /**
   * Fetch users for a specific page without changing global filters
   * @param page The page number to fetch
   * @returns Promise containing the users and pagination info
   */
  async fetchUsersPage(page: number): Promise<{
    users: FabrikUser[];
    totalPages: number;
    currentPage: number;
    totalCount: number;
  }> {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Authentication token not available');
    }
    
    // Use current filters but override the page
    const { email, userId, membersOnly, includeUsageData, orderBy, orderDirection } = this.currentFilters;
    const response = await fetchUsers(token, page, email || undefined, userId || undefined, includeUsageData, membersOnly, orderBy, orderDirection);
    
    // Always add users to store (accumulate)
    if (response.items) {
      response.items.forEach(user => {
        addUser(user);
      });
    }
    
    return {
      users: response.items || [],
      totalPages: response.total_pages || 1,
      currentPage: response.current_page || 1,
      totalCount: response.total_count || 0
    };
  }

  /**
   * Fetch a single user by ID
   * @param userId The ID of the user to fetch
   * @returns Promise containing the user
   */
  async fetchUserById(userId: string): Promise<FabrikUser> {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Authentication token not available');
    }
    
    const user = await fetchUserById(token, userId);
    
    // Update store with fetched user
    if (user) {
      addUser(user);
    }
    
    return user;
  }

  /**
   * Fetch active day passes for a specific user
   * @param userId The ID of the user to fetch day passes for
   * @param bypassCache Optional flag to bypass cache and fetch fresh data
   * @returns Promise containing array of active day passes
   */
  async fetchUserActiveDayPasses(userId: string, bypassCache: boolean = false): Promise<DayPassResponse[]> {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Authentication token not available');
    }

    try {
      const dayPasses = await fetchUserActiveDayPasses(token, userId, bypassCache);
      return dayPasses;
    } catch (error) {
      console.error(`Error fetching day passes for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Get count of active day passes for a specific user
   * @param userId The ID of the user to get count for
   * @param bypassCache Optional flag to bypass cache and fetch fresh data
   * @returns Promise containing the count of active day passes
   */
  async getUserActiveDayPassesCount(userId: string, bypassCache: boolean = false): Promise<number> {
    try {
      const dayPasses = await this.fetchUserActiveDayPasses(userId, bypassCache);
      return dayPasses.length;
    } catch (error) {
      console.error(`Error getting day passes count for user ${userId}:`, error);
      return 0; // Return 0 on error to avoid breaking UI
    }
  }
  
  /**
   * Fetch recent check-ins for a specific user
   * @param userId The ID of the user to fetch check-ins for
   * @param bypassCache Optional flag to bypass cache and fetch fresh data
   * @returns Promise containing array of recent check-ins
   */
  async fetchUserRecentCheckIns(userId: string, bypassCache: boolean = false): Promise<CheckInResponse[]> {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Authentication token not available');
    }

    try {
      const checkIns = await fetchUserRecentCheckIns(token, userId, bypassCache);
      return checkIns;
    } catch (error) {
      console.error(`Error fetching check-ins for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Fetch all membership tiers
   * @param bypassCache Optional flag to bypass cache and fetch fresh data
   * @returns Promise containing array of membership tiers
   */
  async fetchMembershipTiers(bypassCache: boolean = false): Promise<MembershipTierResponse[]> {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Authentication token not available');
    }

    try {
      const tiers = await fetchMembershipTiers(token, bypassCache);
      return tiers;
    } catch (error) {
      console.error('Error fetching membership tiers:', error);
      throw error;
    }
  }

  /**
   * Calculate available days for a user based on their membership tier and recent check-ins
   * @param user The user object containing subscription details
   * @param checkIns Array of recent check-ins for the user
   * @param membershipTiers Array of all membership tiers
   * @returns Number of available days, or null if unlimited/unknown
   */
  calculateAvailableDays(
    user: FabrikUser, 
    checkIns: CheckInResponse[], 
    membershipTiers: MembershipTierResponse[]
  ): number | null {
    // Get user's current membership tier
    const userTierName = user.subscription_details?.[0]?.product_name;
    if (!userTierName) {
      return null; // No subscription
    }

    // Find the tier details
    const userTier = membershipTiers.find(tier => tier.name === userTierName);
    if (!userTier) {
      return null; // Tier not found
    }

    // If days_in_space is null, it means unlimited
    if (userTier.days_in_space === null) {
      return null; // Unlimited
    }

    // Count check-ins that used membership days (not day passes)
    const membershipCheckIns = checkIns.filter(checkIn => 
      checkIn.used_quota_type === 'membership day'
    );

    // Calculate available days
    const availableDays = userTier.days_in_space - membershipCheckIns.length;
    return Math.max(0, availableDays); // Don't return negative numbers
  }

  /**
   * Get available days for a user by fetching their check-ins and membership tiers
   * @param user The user object containing subscription details
   * @returns Promise containing number of available days, or null if unlimited/unknown
   */
  async getUserAvailableDays(user: FabrikUser): Promise<number | null> {
    try {
      // Fetch both check-ins and membership tiers in parallel
      const [checkIns, membershipTiers] = await Promise.all([
        this.fetchUserRecentCheckIns(user.id),
        this.fetchMembershipTiers()
      ]);

      // Calculate available days
      return this.calculateAvailableDays(user, checkIns, membershipTiers);
    } catch (error) {
      console.error(`Error calculating available days for user ${user.id}:`, error);
      throw error;
    }
  }
  
  /**
   * Refresh all user data
   * This is useful for initial data loading and background refreshes
   */
  async refreshAllData(): Promise<void> {
    // Set loading flag
    store.metadata.isLoadingAllUsers = true;
    
    // Clear existing users before loading new filtered results
    clearUsers();
    
    // Reset to page 1 but keep other filters
    const currentFilters = this.getFilters();
    this.setFilters({ ...currentFilters, page: 1 });
    
    try {
      // 1. Load first page (quick render)
      const firstPageResponse = await this.fetchUsers();
      
      // 2. If totalPages > 1, load pages 2-N with concurrency control
      if (firstPageResponse.totalPages > 1) {
        const additionalPagePromises = [];
        for (let page = 2; page <= firstPageResponse.totalPages; page++) {
          // Use fetchUsersPage to avoid race conditions with global filters
          additionalPagePromises.push(
            this.fetchUsersPage(page).catch(error => {
              console.error(`Error loading page ${page} for users:`, error);
              // Return empty response to allow other pages to continue loading
              return {
                users: [],
                totalPages: 0,
                currentPage: page,
                totalCount: 0
              };
            })
          );
        }
        
        // Load additional pages with concurrency control (max 3 concurrent requests)
        // This helps prevent overwhelming the server with too many simultaneous requests
        const results = [];
        const concurrencyLimit = 3;
        
        for (let i = 0; i < additionalPagePromises.length; i += concurrencyLimit) {
          const batch = additionalPagePromises.slice(i, i + concurrencyLimit);
          const batchResults = await Promise.all(batch);
          results.push(...batchResults);
        }
      }
    } catch (error) {
      console.error('Error in refreshAllData:', error);
      
      // Provide more context in the error message
      if (error instanceof Error) {
        throw new Error(`Failed to refresh user data: ${error.message}`);
      } else {
        throw new Error('Failed to refresh user data: Unknown error');
      }
    } finally {
      // 3. Clear loading flag
      store.metadata.isLoadingAllUsers = false;
      
      // Reset to original page
      this.setFilters(currentFilters);
    }
  }
  
  /**
   * Invalidate the users cache
   * Forces a fresh fetch on the next request
   */
  invalidateCache(): void {
    invalidateUsersCache();
  }
}

// Create a singleton instance
export const userDataService = new UserDataService();

// Export the singleton
export default userDataService;
